import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faEye,
  faSyncAlt,
  faFileAlt,
  faFilePdf,
  faLink,
  faSpinner,
  faExclamationTriangle,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { getDprDocuments, refreshDprDocumentUrl } from "../../services/detailedPaymentReportApi";

// ─── helpers ────────────────────────────────────────────────────────────────

const formatDate = (dt) => {
  if (!dt) return "—";
  return new Date(dt).toLocaleString();
};

const fileIcon = (fileType = "") => {
  if (fileType.toLowerCase().includes("pdf")) return faFilePdf;
  return faFileAlt;
};

// ─── FileRow ────────────────────────────────────────────────────────────────

const FileRow = ({ file, onRefresh, refreshing }) => {
  const handleView = () => {
    if (!file.signed_url) return;
    window.open(file.signed_url, "_blank", "noopener,noreferrer");
  };

  return (
    <li className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={fileIcon(file.file_type)}
            className="shrink-0 text-sky-500"
          />
          <span className="truncate font-medium text-slate-700" title={file.original_file_name}>
            {file.original_file_name}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-slate-400">
          Uploaded: {formatDate(file.created_at)}
          {file.url_expires_at && (
            <span className="ml-2">· Expires: {formatDate(file.url_expires_at)}</span>
          )}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {/* View */}
        <button
          type="button"
          onClick={handleView}
          disabled={!file.signed_url}
          title="Open file"
          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <FontAwesomeIcon icon={faEye} />
          View
        </button>

        {/* Refresh Link */}
        <button
          type="button"
          onClick={() => onRefresh(file)}
          disabled={refreshing}
          title="Refresh signed URL"
          className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50"
        >
          <FontAwesomeIcon icon={refreshing ? faSpinner : faLink} spin={refreshing} />
          {refreshing ? "..." : "Refresh Link"}
        </button>
      </div>
    </li>
  );
};

// ─── DprDocumentsModal ───────────────────────────────────────────────────────

const DprDocumentsModal = ({ isOpen, onClose, row }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [loaFiles, setLoaFiles] = useState([]);
  const [soaFiles, setSoaFiles] = useState([]);
  const [otherFileReferences, setOtherFileReferences] = useState([]);
  const [refreshingMap, setRefreshingMap] = useState({});

  // Load documents whenever the modal opens with a row
  useEffect(() => {
    if (!isOpen || !row?.trans_no) return;

    setLoading(true);
    setError("");
    setLoaFiles([]);
    setSoaFiles([]);
    setOtherFileReferences([]);
    setRefreshingMap({});

    getDprDocuments({ trans_no: row.trans_no, soa: row.soa ?? "" })
      .then((res) => {
        setLoaFiles(res.data?.loa_files || []);
        setSoaFiles(res.data?.soa_files || []);
        setOtherFileReferences(
          Array.isArray(res.data?.other_files_reference)
            ? res.data.other_files_reference.filter(
                (item) => item && item.url && item.file_name
              )
            : []
        );
      })
      .catch((err) => {
        setError(
          err?.response?.data?.message ||
            "Failed to load documents. Please try again."
        );
      })
      .finally(() => setLoading(false));
  }, [isOpen, row?.trans_no, row?.soa]);

  const handleRefresh = async (file) => {
    const id = file.upload_id;
    setRefreshingMap((prev) => ({ ...prev, [id]: true }));

    try {
      const res = await refreshDprDocumentUrl(id);
      const data = res.data || {};

      if (data.signed_url) {
        const updater = (prev) =>
          prev.map((f) =>
            f.upload_id === id
              ? { ...f, signed_url: data.signed_url, url_expires_at: data.url_expires_at }
              : f
          );
        setLoaFiles(updater);
        setSoaFiles(updater);
      }
    } catch {
      // silent — file row stays as-is
    } finally {
      setRefreshingMap((prev) => ({ ...prev, [id]: false }));
    }
  };

  if (!isOpen) return null;

  const transNo = row?.trans_no ?? "—";
  const soaLabel = row?.soa ?? "—";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-gradient-to-r from-sky-600 to-blue-700 px-6 py-4 text-white">
          <div>
            <h2 className="text-lg font-bold tracking-tight">View Documents</h2>
            <p className="mt-0.5 text-sm text-sky-100">
              Trans No: <span className="font-semibold">{transNo}</span>
              {row?.soa && (
                <span className="ml-3">
                  SOA / Batch: <span className="font-semibold">{soaLabel}</span>
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mt-0.5 rounded-full p-1.5 text-white/80 hover:bg-white/20 hover:text-white"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-500">
              <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-sky-500" />
              <span className="text-sm">Loading documents…</span>
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {/* LOA / Claim Attachments */}
              <section>
                <h3 className="mb-3 text-sm font-semibold text-slate-700">
                  LOA / Claim Attachments
                  <span className="ml-2 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-bold text-sky-700">
                    {loaFiles.length}
                  </span>
                </h3>
                {loaFiles.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">
                    No LOA / claim attachments found for this transaction.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {loaFiles.map((file) => (
                      <FileRow
                        key={file.upload_id}
                        file={file}
                        onRefresh={handleRefresh}
                        refreshing={!!refreshingMap[file.upload_id]}
                      />
                    ))}
                  </ul>
                )}
              </section>

              {/* SOA Files */}
              <section>
                <h3 className="mb-3 text-sm font-semibold text-slate-700">
                  SOA Files
                  <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">
                    {soaFiles.length}
                  </span>
                </h3>
                {!row?.soa ? (
                  <p className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">
                    No SOA / batch number linked to this transaction.
                  </p>
                ) : soaFiles.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">
                    No SOA files found for batch <strong>{row.soa}</strong>.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {soaFiles.map((file) => (
                      <FileRow
                        key={file.upload_id}
                        file={file}
                        onRefresh={handleRefresh}
                        refreshing={!!refreshingMap[file.upload_id]}
                      />
                    ))}
                  </ul>
                )}
              </section>

              {/* Other File References (v1.0) */}
              <section>
                <h3 className="mb-3 text-sm font-semibold text-slate-700">
                  View Other File References
                  <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-bold text-violet-700">
                    {otherFileReferences.length}
                  </span>
                </h3>
                {otherFileReferences.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">
                    No other file references found from version 1.0.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {otherFileReferences.map((file, index) => (
                      <li
                        key={`${file.source || "reference"}-${file.file_name || "file"}-${index}`}
                        className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faFileAlt} className="shrink-0 text-violet-500" />
                            <span className="truncate font-medium text-slate-700" title={file.file_name}>
                              {file.file_name}
                            </span>
                          </div>
                          <p className="mt-0.5 text-xs text-slate-400">
                            Source: {file.label || file.source || "Reference"}
                          </p>
                        </div>
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 hover:bg-violet-100"
                        >
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                          Open
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-6 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DprDocumentsModal;
